import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

// ====== API URLs ======
const LEAVE_TYPE_BASE_URL = `${API_BASE_PATH}/leave_type`;
const LEAVE_BALANCE_BASE_URL = `${API_BASE_PATH}/leave_balance`;
const DEPARTMENT_BASE_URL = `${API_BASE_PATH}/Admindashboard`; // 🌟 Added Department API base

// ====== Types ======
// POST payload for Leave Type
export interface LeaveTypeCreate {
  leave_type_code: string;
  leave_type_name: string;
  is_paid: boolean;
  max_annual_days: number;
  carry_forward: boolean;
}

// PUT payload for Leave Type
export interface LeaveTypeUpdate {
  leave_type_id: number;
  leave_type_code?: string;
  leave_type_name?: string;
  is_paid?: boolean;
  max_annual_days?: number;
  carry_forward?: boolean;
}

// API response object for Leave Type
export interface LeaveTypeRecord {
  leave_type_id: number;
  leave_type_code: string | null;
  leave_type_name: string | null;
  is_paid: boolean | null;
  max_annual_days: number | null;
  carry_forward: boolean | null;
  created_at: string;
  updated_at: string | null;
}

// Leave Balance Record
export interface LeaveBalanceRecord {
  balance_id: number;
  employee_id: string;
  leave_type_id: number;
  year: number;
  balance_days: number;
  accrued_days: number;
  used_days: number;
  created_at: string;
  updated_at: string | null;
}

// Leave Balance Update Payload
export interface LeaveBalanceUpdate {
  employee_id?: string | null;
  leave_type_id?: number | null;
  year?: number | null;
  balance_days?: number | null;
  accrued_days?: number | null;
  used_days?: number | null;
}

// 🌟 Department Employee Count
export interface DepartmentEmployeeCount {
  department_name: string;
  employee_count: number;
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ====== Leave Type APIs ======

// 🌟 Create Leave Type
export const postLeaveType = async (
  data: LeaveTypeCreate,
  token: string | null
): Promise<LeaveTypeRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await axios.post(
      `${LEAVE_TYPE_BASE_URL}/`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Leave type created successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Update Leave Type
export const putLeaveType = async (
  data: LeaveTypeUpdate,
  token: string | null
): Promise<LeaveTypeRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await axios.put(
      `${LEAVE_TYPE_BASE_URL}/`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Leave type updated successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Get All Leave Types
export const getAllLeaveTypes = async (
  token?: string | null
): Promise<LeaveTypeRecord[]> => {
  try {
    const response = await axios.get(`${LEAVE_TYPE_BASE_URL}/all`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// ====== Leave Balance APIs ======

// 🌟 Get All Leave Balances
export const getAllLeaveBalances = async (
  token: string | null
): Promise<LeaveBalanceRecord[]> => {
  try {
    const response = await axios.get(`${LEAVE_BALANCE_BASE_URL}/get_all`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Get Leave Balance by Employee ID
export const getLeaveBalanceByEmployee = async (
  employeeId: string,
  token: string | null
): Promise<LeaveBalanceRecord[]> => {
  try {
    const response = await axios.get(
      `${LEAVE_BALANCE_BASE_URL}/get_all/${employeeId}`,
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// 🌟 Update Leave Balance
export const putLeaveBalance = async (
  balanceId: number,
  data: LeaveBalanceUpdate,
  token: string | null
): Promise<LeaveBalanceRecord> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.put(
      `${LEAVE_BALANCE_BASE_URL}/update/${balanceId}`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Leave balance updated successfully");
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// ====== Department APIs ======

// 🌟 Get Department-wise Employee Count
export const getDepartmentEmployeeCounts = async (
  token: string | null
): Promise<DepartmentEmployeeCount[]> => {
  try {
    const response = await axios.get(
      `${DEPARTMENT_BASE_URL}/departments-with-employee-count`,
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};
